/**
 * Paso 1 de la activación (T033): aviso de privacidad versionado (FR-007, research R-7).
 *
 * El texto se embebe en el build (`privacy-notice.es-PE.md`); la **versión vigente** la manda la API
 * en `ActivationInfo.privacy_notice.version` y se envía tal cual en `POST /activation/{token}/privacy`
 * (si no coincide con la del servidor, la API responde `400 VALIDATION_ERROR` en `version`).
 *
 * Sin aceptación no hay activación: no existe un botón para saltar este paso.
 *
 * Piloto 2026-09-30: "Aceptar y continuar" se habilita solo al llegar al final del texto
 * (`useReachedEnd`, centinela tras el aviso). Mientras tanto queda `aria-disabled` (sigue siendo
 * enfocable y el lector de pantalla anuncia la pista) y el pie muestra "Desliza hasta el final…".
 */
import { ArrowDown, ShieldCheck } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Screen } from '../../components/ui/screen';
import { Stepper } from '../../components/ui/stepper';
import { t } from '../../i18n';

import { useAcceptActivationPrivacy } from './api';
import { PrivacyNoticeText } from './privacy-notice';
import { useReachedEnd } from './use-reached-end';

export interface PrivacyStepProps {
  token: string;
  /** Versión vigente según la API. */
  version: string;
  onAccepted: () => void;
  onGone: () => void;
  /** 005 · QA-6: `428 DOCUMENT_VERIFICATION_REQUIRED` (no debería pasar desde el flujo normal). */
  onVerificationRequired?: () => void;
}

export function PrivacyStep({
  token,
  version,
  onAccepted,
  onGone,
  onVerificationRequired,
}: PrivacyStepProps) {
  const accept = useAcceptActivationPrivacy(token);
  const { sentinelRef, reached } = useReachedEnd();

  function submit(): void {
    if (!reached) {
      return;
    }
    accept.mutate(version, {
      onSuccess: onAccepted,
      onError: (error) => {
        if (error.is('INVITATION_INVALID') || error.is('INVITATION_EXPIRED')) {
          onGone();
          return;
        }
        if (error.is('DOCUMENT_VERIFICATION_REQUIRED')) {
          onVerificationRequired?.();
        }
      },
    });
  }

  const errorMessage =
    accept.error && !accept.error.is('INVITATION_INVALID') && !accept.error.is('INVITATION_EXPIRED')
      ? accept.error.message
      : undefined;

  return (
    <Screen
      data-testid="activation-privacy"
      header={<Stepper current={1} />}
      title={t('activation.privacy.title')}
      lead={t('activation.privacy.lead')}
      footer={
        <>
          {errorMessage ? (
            <p role="alert" className="text-fm-sm text-fm-danger-text">
              {errorMessage}
            </p>
          ) : null}
          {reached ? null : (
            <p
              id="privacy-scroll-hint"
              data-testid="privacy-scroll-hint"
              className="flex items-center justify-center gap-fm-2 text-center text-fm-sm text-fm-text-secondary"
            >
              <ArrowDown aria-hidden="true" size={16} className="shrink-0" />
              {t('activation.privacy.scrollHint')}
            </p>
          )}
          <Button
            data-testid="privacy-accept"
            aria-disabled={reached ? undefined : true}
            aria-describedby={reached ? undefined : 'privacy-scroll-hint'}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-45"
            loading={accept.isPending}
            loadingText={t('activation.privacy.sending')}
            icon={<ShieldCheck aria-hidden="true" size={20} />}
            onClick={submit}
          >
            {t('activation.privacy.action')}
          </Button>
        </>
      }
    >
      <p data-testid="privacy-version" className="text-fm-sm text-fm-text-secondary">
        {t('activation.privacy.version', { version })}
      </p>
      <PrivacyNoticeText />
      <div ref={sentinelRef} data-testid="privacy-end" aria-hidden="true" className="h-px" />
    </Screen>
  );
}
